import React, { useEffect } from "react";
import { X, MapPin, Check, ArrowRight, Star } from "lucide-react";

export default function ProductDetailModal({ product, onClose, onContact }) {
  useEffect(() => {
    if (!product) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [product, onClose]);

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="product-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white w-full md:max-w-3xl md:rounded-3xl rounded-t-3xl max-h-[95vh] md:max-h-[88vh] overflow-y-auto shadow-2xl"
        style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1)" }}
        data-testid="product-modal"
      >
        {/* Hero banner */}
        <div className="relative h-44 md:h-56 overflow-hidden">
          <img src={product.img} alt={product.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#3B0A11]/80 via-[#6B0F1A]/50 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
            <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">
              Products · {product.title}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight mt-1">
              {product.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 text-white flex items-center justify-center"
            data-testid="close-product-modal"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 space-y-6">
          <p className="text-stone-600 text-base leading-relaxed">{product.tagline}</p>

          {/* Certification pills */}
          <div className="flex flex-wrap gap-2">
            {product.certs.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 bg-[#F4E8E9] text-[#6B0F1A] text-xs font-semibold rounded-full px-3 py-1.5 border border-[#6B0F1A]/10">
                <Check className="w-3.5 h-3.5" /> {c}
              </span>
            ))}
          </div>

          {/* Available products */}
          <div>
            <h3 className="font-display text-2xl font-bold tracking-tight text-[#1F1F1F]">
              {product.itemsLabel || "Available Products"}
            </h3>
            <p className="text-stone-500 text-sm mt-1">
              {product.itemsIntro || `Our ${product.title.toLowerCase()} portfolio, sourced directly from verified origins.`}
            </p>

            <div className="mt-5 space-y-3">
              {product.items.map((it, i) => (
                <div
                  key={it.name}
                  className="relative rounded-2xl border border-stone-200 bg-gradient-to-br from-white to-stone-50 p-5 hover-lift"
                  style={{ animation: `fadeUp 0.5s ${0.05 + i * 0.07}s both ease-out` }}
                  data-testid={`product-item-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h4 className="font-display text-xl font-bold tracking-tight text-[#6B0F1A] flex items-center gap-2">
                      {it.name}
                      {it.featured && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#6B0F1A] text-white rounded-full px-2 py-0.5">
                          <Star className="w-3 h-3" fill="currentColor" /> Featured
                        </span>
                      )}
                    </h4>
                  </div>
                  <div className="flex items-start gap-1.5 mt-2 text-sm text-stone-600">
                    <MapPin className="w-4 h-4 text-[#A8323F] mt-0.5 shrink-0" />
                    <span>{it.origins}</span>
                  </div>
                  <div className="mt-2 text-sm text-stone-700 font-medium">
                    {it.variety}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA block */}
          <div className="relative rounded-2xl bg-gradient-to-br from-[#6B0F1A] to-[#3B0A11] p-6 md:p-8 text-white overflow-hidden">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#A8323F]/20 blur-2xl" />
            <div className="relative">
              <h3 className="font-display text-2xl font-bold tracking-tight">Interested in {product.title}?</h3>
              <p className="text-white/75 text-sm mt-2 max-w-md">
                Connect with our trade desk to discuss specifications, volumes, and delivery schedules.
              </p>
              <button
                onClick={() => { onClose(); onContact?.(product.slug); }}
                className="mt-5 inline-flex items-center gap-2 bg-white text-[#6B0F1A] font-semibold rounded-xl px-5 py-3 hover:bg-[#F4E8E9] shine"
                data-testid="product-cta-contact"
              >
                {product.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
